/**
 * TrialConsentDialog — iter-176 (shipped 2026-02-04, updated 2026-10-14).
 *
 * BEFORE a card form is shown (and before we redirect to Stripe
 * Checkout), surface the exact spec-required disclosure and require an
 * affirmative-consent checkbox the user must tick. The checkbox must
 * NOT be pre-checked — the spec is explicit. Only after confirmation
 * does the parent's ``onConfirm`` handler proceed with the actual
 * checkout call. Grandfathered employers (30-day no-card trial) bypass
 * this gate entirely — the parent chooses whether to render us.
 */
import React, { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription,
    DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";

const PLAN_PRICE_BY_KEY = {
    employer_starter_month: 99,
    employer_growth_month: 249,
};

export const TrialConsentDialog = ({ open, onOpenChange, planKey, planName, onConfirm, busy = false }) => {
    const price = PLAN_PRICE_BY_KEY[planKey] ?? null;
    const priceLabel = price ? `$${price}/month` : "the monthly plan price";
    const [consented, setConsented] = useState(false);
    const checkboxRef = useRef(null);

    // Reset the checkbox every time the dialog opens — the spec forbids
    // a pre-checked state, including when the user re-opens the gate.
    useEffect(() => {
        if (open) setConsented(false);
    }, [open]);

    const canConfirm = consented && !busy;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="trial-consent-dialog" className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle data-testid="trial-consent-title">
                        Before you continue — {planName || "trial"} terms
                    </DialogTitle>
                    <DialogDescription asChild>
                        <div className="space-y-3 text-sm text-foreground">
                            <p data-testid="trial-consent-disclosure" className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                                14-day free trial. Your card will be charged <strong>{priceLabel}</strong> on day 15 unless you cancel before then. Cancel anytime in your account — no charge if you cancel before day 15.
                            </p>
                            <p className="text-xs text-muted-foreground">
                                Your card is collected on the next screen via Stripe. Simonara never sees or stores your card number.
                            </p>
                        </div>
                    </DialogDescription>
                </DialogHeader>

                <label
                    htmlFor="trial-consent-check"
                    className="mt-2 flex items-start gap-3 rounded-md border border-border bg-card p-3 cursor-pointer"
                >
                    <Checkbox
                        id="trial-consent-check"
                        ref={checkboxRef}
                        checked={consented}
                        onCheckedChange={(v) => setConsented(v === true)}
                        data-testid="trial-consent-checkbox"
                        className="mt-0.5"
                    />
                    <span className="text-sm text-foreground leading-relaxed" data-testid="trial-consent-label">
                        I understand my card will be charged <strong>{priceLabel}</strong> starting on day 15 unless I cancel before then.
                    </span>
                </label>

                <DialogFooter className="mt-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        data-testid="trial-consent-cancel"
                        disabled={busy}
                    >
                        Not yet
                    </Button>
                    <Button
                        type="button"
                        onClick={onConfirm}
                        disabled={!canConfirm}
                        aria-disabled={!canConfirm}
                        data-testid="trial-consent-confirm"
                    >
                        {busy ? "Opening Stripe…" : "Continue to card entry"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default TrialConsentDialog;
